// Spor: tillid. Driftsvagtens faste opgave: frekvensnålen.
//
// Co-op runde 4: "Driftsvagten sidder og kigger på mellem stormene". Nettet svinger altid lidt om
// 50,00 Hz. På en bane med roller glider nålen af sig selv, og Driftsvagten trykker "Frekvens" for at
// regulere den tilbage (som en manuel reserve i kontrolrummet). Inden for 49,9-50,1 Hz (FCR-N-båndet)
// er alt fint. Uden for båndet giver et tryk 1 kr (reserven blev brugt i tide). Over 200 mHz væk
// koster reserven 1 kr hver 3. sekund, til nogen regulerer. Aldrig hjerter: nålen er orange, ikke rød.
//
// Kun baner med roller (Volt solo og to mennesker), så solo-hashen er uændret. Ingen fælles
// tilfældighed: nålens vej er en hash af seedet og afsnittets nummer, så rng-strømmene er urørte.
// Mens en storm, gravko eller ankerbåd er varslet, står nålen stille: så har Driftsvagten et rigtigt job.
// Et værk, der falder ud, trækker nålen ned til frekvensens bund (freq_dip).

import type { HzState, PlayerId, SimEvent, State } from './types';
import { TICK_HZ } from './types';

/** mHz fra 50,00 Hz, hvor nålen er grøn (FCR-N-båndet 49,9-50,1 Hz). */
export const HZ_GREEN = 100;
/** Herfra koster reserven penge. */
export const HZ_ORANGE = 200;
/** Skalaens kant: nålen går ikke længere ud. */
export const HZ_MAX = 300;
/** Så meget trækker ét tryk nålen tilbage mod 50,00 Hz. */
export const HZ_TRIM = 150;
/** Tættere på 50,00 Hz er der intet at regulere. */
export const HZ_TRIM_MIN = 30;
/** Ticks mellem to tryk (reserven skal lige nå at virke). */
export const HZ_COOLDOWN = 15;
/** Kr for et tryk uden for det grønne bånd. */
export const HZ_BONUS = 1;
/** Uden for 200 mHz: 1 kr pr. så mange ticks. */
export const HZ_COST_TICKS = 3 * TICK_HZ;


/** Ny nål, midt i det grønne. */
export function newHz(): HzState {
  return { dev: 0, seg: 0, segEnd: 0, rate: 0, readyAt: 0, outTicks: 0, trims: 0, earned: 0, paid: 0 };
}

/** Et 32-bit tal ud af (seed, n). Rører ikke State. */
function mix(seed: number, n: number): number {
  let z = (Math.imul(seed >>> 0, 0x9e3779b1) ^ Math.imul(n + 0x485a, 0x85ebca6b)) >>> 0;
  z = Math.imul(z ^ (z >>> 16), 0x7feb352d) >>> 0;
  z = Math.imul(z ^ (z >>> 15), 0x846ca68b) >>> 0;
  return (z ^ (z >>> 16)) >>> 0;
}

/**
 * Afsnit n af nålens vej: 6-14 s i én retning med 1 mHz pr. tick (hvert fjerde afsnit 2). Fra midten tager det typisk 10-25 s,
 * før nålen er ude af det grønne. Står den allerede langt ude, glider den oftere videre væk end hjem.
 */
export function hzSegment(seed: number, n: number, dev: number): { rate: number; ticks: number } {
  const r = mix(seed, n);
  const ticks = (6 + (r % 9)) * TICK_HZ;
  const speed = ((r >>> 3) % 4) === 0 ? 2 : 1;
  const away = ((r >>> 5) % 100) < 65;
  const dir = Math.abs(dev) < HZ_TRIM_MIN ? (((r >>> 12) & 1) === 0 ? 1 : -1) : (away ? Math.sign(dev) : -Math.sign(dev));
  return { rate: dir * speed, ticks };
}

/** Er en trussel varslet, som Driftsvagten skal tage sig af? Så står nålen stille. */
function driftBusy(state: State): boolean {
  return state.threats.some((t) => t.phase === 'warning' && (t.kind === 'storm' || t.kind === 'digger' || t.kind === 'anchor' || t.kind === 'duo'));
}

export type HzZone = 'green' | 'yellow' | 'orange';

export function hzZone(dev: number): HzZone {
  const a = Math.abs(dev);
  return a <= HZ_GREEN ? 'green' : a <= HZ_ORANGE ? 'yellow' : 'orange';
}

/** Frekvensen som tekst: "50,08 Hz". */
export function hzText(dev: number): string {
  return `${(50 + dev / 1000).toFixed(2).replace('.', ',')} Hz`;
}

/** Kan et tryk på "Frekvens" gøre noget nu? Fejlkoden som i check(), ellers null. */
export function hzBlocked(state: State): 'not_allowed' | 'not_needed' | 'busy' | null {
  const hz = state.hz;
  if (!state.roles || !hz) return 'not_allowed';
  if (Math.abs(hz.dev) < HZ_TRIM_MIN) return 'not_needed';
  if (state.tick < hz.readyAt) return 'busy';
  return null;
}

/** Ét tryk: nålen tilbage mod 50,00 Hz. Uden for det grønne bånd giver det en krone. */
export function trimHz(state: State, player: PlayerId, events: SimEvent[]): void {
  const hz = state.hz;
  if (!hz) return;
  const before = hz.dev;
  const bonus = Math.abs(before) > HZ_GREEN ? HZ_BONUS : 0;
  hz.dev = Math.sign(before) * Math.max(0, Math.abs(before) - HZ_TRIM);
  hz.readyAt = state.tick + HZ_COOLDOWN;
  hz.outTicks = 0;
  hz.trims++;
  hz.earned += bonus;
  state.budget += bonus;
  // Nålen har fået et skub hjem: næste afsnit begynder forfra herfra.
  hz.segEnd = state.tick;
  events.push({ t: 'hz_trim', tick: state.tick, from: before, dev: hz.dev, bonus, by: player });
}

/** Ét tick af nålen. Kaldes i step() efter truslerne, så et værksnedbrud i samme tick ses. */
export function updateHz(state: State, events: SimEvent[]): void {
  if (!state.roles) return;
  const hz = (state.hz ??= newHz());
  const was = hzZone(hz.dev);
  for (const e of events) {
    // Værket faldt ud: frekvensen dykker til bunden (centihertz -> mHz).
    if (e.t === 'freq_dip') hz.dev = Math.max(-HZ_MAX, Math.min(hz.dev, (e.nadir - 5000) * 10));
  }
  if (!driftBusy(state)) {
    if (state.tick >= hz.segEnd) {
      const s = hzSegment(state.seed, hz.seg++, hz.dev);
      hz.rate = s.rate;
      hz.segEnd = state.tick + s.ticks;
    }
    hz.dev = Math.max(-HZ_MAX, Math.min(HZ_MAX, hz.dev + hz.rate));
    if (hzZone(hz.dev) === 'orange') {
      hz.outTicks++;
      if (hz.outTicks >= HZ_COST_TICKS) {
        hz.outTicks = 0;
        if (state.budget > 0) {
          state.budget--;
          hz.paid++;
        }
      }
    } else hz.outTicks = 0;
  }
  if (was !== 'orange' && hzZone(hz.dev) === 'orange') events.push({ t: 'hz_out', tick: state.tick, dev: hz.dev });
}
